import React from 'react';

// 3D Cube Icon Component
export const Cube3D = ({ color = "#8B3A3A", size = 80 }) => (
  <div className="relative inline-block" style={{ width: size, height: size }}>
    <div className="absolute inset-0 transform-3d animate-spin-3d" style={{ transformStyle: 'preserve-3d' }}>
      {/* Front face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          transform: 'translateZ(40px)'
        }}
      />
      {/* Back face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color}99 0%, ${color}66 100%)`,
          transform: 'translateZ(-40px) rotateY(180deg)'
        }}
      />
      {/* Right face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color}cc 0%, ${color}88 100%)`,
          transform: 'rotateY(90deg) translateZ(40px)'
        }}
      />
      {/* Left face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color}bb 0%, ${color}77 100%)`,
          transform: 'rotateY(-90deg) translateZ(40px)'
        }}
      />
      {/* Top face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color}ee 0%, ${color}aa 100%)`,
          transform: 'rotateX(90deg) translateZ(40px)'
        }}
      />
      {/* Bottom face */}
      <div 
        className="absolute inset-0 border-2 border-white/30 rounded-lg"
        style={{ 
          background: `linear-gradient(135deg, ${color}aa 0%, ${color}66 100%)`,
          transform: 'rotateX(-90deg) translateZ(40px)'
        }}
      />
    </div>
  </div>
);

// 3D Pyramid Icon
export const Pyramid3D = ({ color = "#8B3A3A", size = 80 }) => (
  <div className="relative inline-block animate-float-3d" style={{ width: size, height: size }}>
    <div className="absolute inset-0 transform-3d" style={{ transformStyle: 'preserve-3d', transform: 'rotateX(-20deg) rotateY(45deg)' }}>
      {/* Base */}
      <div 
        className="absolute"
        style={{
          width: size,
          height: size,
          background: `linear-gradient(135deg, ${color}aa 0%, ${color}66 100%)`,
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
          transform: 'translateZ(0px)'
        }}
      />
      {/* Left face */}
      <div 
        className="absolute"
        style={{
          width: size,
          height: size,
          background: `linear-gradient(135deg, ${color}dd 0%, ${color}99 100%)`,
          clipPath: 'polygon(50% 0%, 0% 100%, 50% 70%)',
          transform: 'rotateY(-30deg) translateZ(20px)'
        }}
      />
      {/* Right face */}
      <div 
        className="absolute"
        style={{
          width: size,
          height: size,
          background: `linear-gradient(135deg, ${color}cc 0%, ${color}88 100%)`,
          clipPath: 'polygon(50% 0%, 100% 100%, 50% 70%)',
          transform: 'rotateY(30deg) translateZ(20px)'
        }}
      />
    </div>
  </div>
);

// 3D Sphere Icon
export const Sphere3D = ({ color = "#8B3A3A", size = 80 }) => (
  <div 
    className="relative inline-block rounded-full animate-pulse-3d"
    style={{ 
      width: size, 
      height: size,
      background: `radial-gradient(circle at 30% 30%, ${color}ff 0%, ${color}cc 40%, ${color}66 100%)`,
      boxShadow: `
        inset -10px -10px 20px ${color}33,
        inset 10px 10px 20px ${color}ff,
        0 10px 30px ${color}66
      `
    }}
  >
    <div 
      className="absolute top-[20%] left-[20%] w-[30%] h-[30%] rounded-full bg-white/40 blur-sm"
    />
  </div>
);

// 3D Panel Component
export const Panel3D = ({ children, className = "", depth = 20 }) => (
  <div 
    className={`relative transform-3d ${className}`}
    style={{ transformStyle: 'preserve-3d' }}
  >
    {/* Main panel */}
    <div 
      className="relative bg-white/90 backdrop-blur-xl rounded-2xl border-2 border-slate-200/50 shadow-2xl"
      style={{ transform: `translateZ(${depth}px)` }}
    >
      {children}
    </div>
    {/* Depth layers */}
    {[...Array(3)].map((_, i) => (
      <div
        key={i}
        className="absolute inset-0 bg-slate-300/20 rounded-2xl -z-10"
        style={{
          transform: `translateZ(${-5 * (i + 1)}px)`,
          filter: `blur(${i}px)`
        }}
      />
    ))}
  </div>
);

// 3D Isometric Icon Container
export const IsometricContainer = ({ children, color = "#8B3A3A" }) => (
  <div className="relative inline-block perspective-container">
    <div 
      className="relative p-6 transform-3d"
      style={{
        transform: 'rotateX(30deg) rotateY(-30deg)',
        transformStyle: 'preserve-3d'
      }}
    >
      {/* Top face */}
      <div 
        className="absolute inset-0 rounded-lg border-2 border-white/30"
        style={{
          background: `linear-gradient(135deg, ${color}ee 0%, ${color}bb 100%)`,
          transform: 'translateZ(30px)'
        }}
      >
        <div className="flex items-center justify-center h-full">
          {children}
        </div>
      </div>
      {/* Right side */}
      <div 
        className="absolute top-0 right-0 w-8 h-full rounded-r-lg border-2 border-white/30"
        style={{
          background: `linear-gradient(180deg, ${color}aa 0%, ${color}77 100%)`,
          transform: 'rotateY(90deg) translateZ(30px) translateX(-4px)'
        }}
      />
      {/* Bottom side */}
      <div 
        className="absolute bottom-0 left-0 w-full h-8 rounded-b-lg border-2 border-white/30"
        style={{
          background: `linear-gradient(90deg, ${color}99 0%, ${color}66 100%)`,
          transform: 'rotateX(-90deg) translateZ(30px) translateY(4px)'
        }}
      />
    </div>
  </div>
);

// 3D Card with Extrusion
export const Card3D = ({ children, className = "" }) => (
  <div className={`relative transform-3d ${className}`} style={{ transformStyle: 'preserve-3d' }}>
    {/* Main card face */}
    <div 
      className="relative bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-2xl border-2 border-slate-200/50"
      style={{ transform: 'translateZ(30px)' }}
    >
      {children}
    </div>
    
    {/* Card edges for 3D effect */}
    <div 
      className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl -z-10"
      style={{ transform: 'translateZ(20px)' }}
    />
    <div 
      className="absolute inset-0 bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl -z-20"
      style={{ transform: 'translateZ(10px)' }}
    />
    <div 
      className="absolute inset-0 bg-gradient-to-br from-slate-400 to-slate-500 rounded-2xl -z-30"
      style={{ transform: 'translateZ(0px)' }}
    />
  </div>
);

// 3D Hexagon
export const Hexagon3D = ({ color = "#8B3A3A", size = 100 }) => (
  <div 
    className="relative inline-block animate-tilt-3d"
    style={{ width: size, height: size }}
  >
    <svg width={size} height={size} viewBox="0 0 100 100" className="transform-3d" style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))' }}>
      <defs>
        <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.6 }} />
        </linearGradient>
      </defs>
      <polygon 
        points="50,5 90,30 90,70 50,95 10,70 10,30" 
        fill="url(#hexGrad)"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="2"
      />
      <polygon 
        points="50,5 90,30 50,50" 
        fill="rgba(255,255,255,0.2)"
      />
    </svg>
  </div>
);

// 3D Ring/Torus
export const Ring3D = ({ color = "#8B3A3A", size = 80 }) => (
  <div 
    className="relative inline-block animate-spin-3d"
    style={{ width: size, height: size }}
  >
    <div 
      className="absolute inset-0 rounded-full border-8"
      style={{
        borderColor: `${color} ${color}66 ${color}33 ${color}99`,
        boxShadow: `
          inset 0 5px 15px ${color}66,
          0 10px 30px ${color}33
        `,
        transform: 'rotateX(70deg)'
      }}
    />
  </div>
);

export default {
  Cube3D,
  Pyramid3D,
  Sphere3D,
  Panel3D,
  IsometricContainer,
  Card3D,
  Hexagon3D,
  Ring3D
};